import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listProductTours,
  setProductTourState,
  type ProductTourPublic,
  type ProductTourPublicList,
  type ProductTourUserState,
} from '@/api'
import { useAuth } from '@/hooks/use-auth'
import useLang from '@/hooks/use-lang'
import type {
  ProductTour,
  ProductTourPlacement,
  ProductTourStatus,
} from '@/lib/productTours/types'

type TourLocale = 'fr' | 'en' | 'de'

/**
 * Statut d'un tour pour l'utilisateur, lu sur le type de l'API : son énumération est anonyme dans le
 * contrat, et oazapfts la renumérote (`Status8`, `Status9`…) à chaque régénération.
 */
type ProductTourStateStatus = NonNullable<ProductTourUserState>['status']

const tourLocale = (lang: string): TourLocale =>
  lang === 'fr' || lang === 'de' ? lang : 'en'

/** Tours publiés et visibles pour l'utilisateur, déjà dans sa langue (gateway). */
export const productToursQueryKey = (
  userId: string | null | undefined,
  locale: TourLocale
) => ['product-tours', userId ?? null, locale] as const

const toTour = (tour: ProductTourPublic): ProductTour => ({
  id: tour.id,
  key: tour.key,
  version: tour.version,
  contextKey: tour.contextKey,
  autoStart: tour.autoStart,
  title: tour.title,
  state: tour.state
    ? {
        status: tour.state.status as ProductTourStatus,
        tourVersion: tour.state.tourVersion,
      }
    : null,
  steps: tour.steps.map((step) => ({
    ...step,
    placement: step.placement as ProductTourPlacement,
  })),
})

export type UseProductToursResult = {
  tours: ProductTour[]
  setTourState: (tour: ProductTour, status: ProductTourStatus) => void
}

/**
 * `enabled` : flag `product_tours` visible. Les routes portent `x-feature-flag` côté gateway et
 * répondent 403 sans lui : on ne les appelle pas.
 */
export const useProductTours = (enabled: boolean): UseProductToursResult => {
  const { isAuthenticated, user } = useAuth()
  const { lang } = useLang()
  const queryClient = useQueryClient()
  const locale = tourLocale(lang)
  const queryKey = productToursQueryKey(user?.uid, locale)

  const { data } = useQuery({
    queryKey,
    queryFn: () => listProductTours({ locale }),
    enabled: enabled && isAuthenticated,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })

  const tours = useMemo(() => (data?.data ?? []).map(toTour), [data])

  const { mutate } = useMutation({
    mutationFn: ({
      tour,
      status,
    }: {
      tour: ProductTour
      status: ProductTourStatus
    }) =>
      setProductTourState(tour.id, {
        status: status as ProductTourStateStatus,
      }),
    /* Optimiste : un tour fermé ne doit pas redémarrer seul avant la réponse. */
    onMutate: ({ tour, status }) => {
      queryClient.setQueryData<ProductTourPublicList>(queryKey, (current) =>
        current
          ? {
              ...current,
              data: current.data.map((candidate) =>
                candidate.id === tour.id
                  ? {
                      ...candidate,
                      state: {
                        status: status as ProductTourStateStatus,
                        tourVersion: tour.version,
                      },
                    }
                  : candidate
              ),
            }
          : current
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })

  const setTourState = useCallback(
    (tour: ProductTour, status: ProductTourStatus) => mutate({ tour, status }),
    [mutate]
  )

  return { tours, setTourState }
}
